const BASE = (process.env.REACT_APP_API_URL || '') + '/api';

// ─── Persistent auth storage ──────────────────────────────────────────────────

export function getAuth() {
  return {
    gcToken:        localStorage.getItem("gc_token"),
    gcDeviceId:     localStorage.getItem("gc_device_id"),
    gcTokenExpires: Number(localStorage.getItem("gc_token_expires")) || 0,
    refreshToken:   localStorage.getItem("gc_refresh_token"),
    refreshExpires: Number(localStorage.getItem("gc_refresh_expires")) || 0,
    userId:         localStorage.getItem("gc_user_id"),
  };
}

export function setAuth({ gcToken, gcDeviceId, gcTokenExpires, refreshToken, refreshExpires, userId }) {
  localStorage.setItem("gc_token", gcToken);
  localStorage.setItem("gc_device_id", gcDeviceId);

  // Read expiry from the JWT itself — GC's gcTokenExpires field is unreliable (often 1hr)
  // The actual JWT exp is typically 14 days
  let expires = gcTokenExpires;
  try {
    const payload = JSON.parse(atob(gcToken.split('.')[1]));
    if (payload.exp) expires = payload.exp;
  } catch (e) { /* fall back to gcTokenExpires */ }

  if (expires) localStorage.setItem("gc_token_expires", String(expires));
  if (refreshToken)  localStorage.setItem("gc_refresh_token", refreshToken);
  if (refreshExpires) localStorage.setItem("gc_refresh_expires", String(refreshExpires));
  if (userId) localStorage.setItem("gc_user_id", userId);
}

export function clearAuth() {
  localStorage.removeItem("gc_token");
  // Keep gc_device_id — GC uses it to recognize trusted devices and skip MFA
  localStorage.removeItem("gc_token_expires");
  localStorage.removeItem("gc_refresh_token");
  localStorage.removeItem("gc_refresh_expires");
  localStorage.removeItem("gc_user_id");
}

export function isAuthenticated() {
  const { gcToken, gcDeviceId, gcTokenExpires } = getAuth();
  if (!gcToken || !gcDeviceId) return false;
  if (gcTokenExpires && Date.now() / 1000 > gcTokenExpires) return false;
  return true;
}

export function isExpired() {
  const { gcToken, gcTokenExpires } = getAuth();
  if (!gcToken) return false;
  return gcTokenExpires > 0 && Date.now() / 1000 > gcTokenExpires;
}

// ─── Token refresh ────────────────────────────────────────────────────────────
// Silently refreshes the access token using the stored refresh token.
// Called automatically before requests when the token is close to expiry.

let _refreshPromise = null;

async function maybeRefresh() {
  const { gcToken, gcTokenExpires, refreshToken, refreshExpires, gcDeviceId } = getAuth();
  if (!gcToken || !refreshToken) return;

  const now = Date.now() / 1000;
  // Refresh if token expires within 5 minutes
  if (!gcTokenExpires || gcTokenExpires - now > 300) return;
  // Don't try if refresh token itself is expired
  if (refreshExpires && now > refreshExpires) return;

  // Deduplicate concurrent refresh calls
  if (!_refreshPromise) {
    _refreshPromise = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken, deviceId: gcDeviceId }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.gcToken) setAuth({ ...getAuth(), ...data });
      })
      .catch(() => {/* silent — will fall through to expired state */})
      .finally(() => { _refreshPromise = null; });
  }
  await _refreshPromise;
}

// ─── HTTP helper ──────────────────────────────────────────────────────────────

async function request(path, options = {}) {
  // Auto-refresh if token is nearly expired
  if (path !== '/auth/refresh') await maybeRefresh();

  const { gcToken, gcDeviceId } = getAuth();
  const isGet = !options.method || options.method === "GET";
  const headers = {
    ...(isGet ? {} : { "Content-Type": "application/json" }),
    ...(gcToken    ? { "X-GC-Token":     gcToken    } : {}),
    ...(gcDeviceId ? { "X-GC-Device-ID": gcDeviceId } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(`Server returned non-JSON response (${res.status}) — is the backend running?`);
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const api = {
  authStart:    (email)               => request("/auth/start",    { method: "POST", body: JSON.stringify({ email, deviceId: getAuth().gcDeviceId || undefined }) }),
  authVerify:   (sessionId, code)     => request("/auth/verify",   { method: "POST", body: JSON.stringify({ sessionId, code }) }),
  authComplete: (sessionId, password) => request("/auth/complete", { method: "POST", body: JSON.stringify({ sessionId, password }) }),

  getTeams:        ()         => request("/teams",                    { method: "GET" }),
  getGames:        (teamId)   => request(`/teams/${teamId}/games`,     { method: "GET" }),

  getSeasonStats:  (teamId, games) => request("/season-stats",  { method: "POST", body: JSON.stringify({ teamId, games }) }),
  getGameBoxScore: (gameId)        => request("/game-boxscore", { method: "POST", body: JSON.stringify({ gameId }) }),
};
